import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  CalendarClock,
  Package,
  Users,
  FlaskConical,
  ScrollText,
  ShoppingBag,
  Camera,
  Settings,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/lib/useAuth';

interface NavIconProps {
  to: string;
  icon: React.ReactNode;
  label: string;
}

function NavIcon({ to, icon, label }: NavIconProps) {
  const location = useLocation();
  const isActive = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
        isActive ? 'bg-white/20' : 'hover:bg-white/10'
      }`}
      title={label}
    >
      {icon}
    </NavLink>
  );
}

export function AdminLayout() {
  const { signOut } = useAuth();

  const iconSize = 20;
  const iconColor = 'white';

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-16 bg-brand-dark flex flex-col items-center py-4 shrink-0">
        {/* Top nav icons */}
        <nav className="flex flex-col items-center gap-3 flex-1">
          <NavIcon
            to="/"
            icon={<LayoutDashboard size={iconSize} color={iconColor} />}
            label="Dashboard"
          />
          <NavIcon
            to="/analytics"
            icon={<BarChart3 size={iconSize} color={iconColor} />}
            label="Analytics"
          />
          <NavIcon
            to="/deliveries"
            icon={<CalendarClock size={iconSize} color={iconColor} />}
            label="Deliveries"
          />
          <NavIcon
            to="/orders"
            icon={<Package size={iconSize} color={iconColor} />}
            label="Orders"
          />
          <NavIcon
            to="/subscribers"
            icon={<Users size={iconSize} color={iconColor} />}
            label="Subscribers"
          />
          <NavIcon
            to="/studio"
            icon={<FlaskConical size={iconSize} color={iconColor} />}
            label="Studio"
          />
          <NavIcon
            to="/audit"
            icon={<ScrollText size={iconSize} color={iconColor} />}
            label="Audit Log"
          />
          <NavIcon
            to="/products"
            icon={<ShoppingBag size={iconSize} color={iconColor} />}
            label="Products"
          />
          <NavIcon
            to="/community"
            icon={<Camera size={iconSize} color={iconColor} />}
            label="Community Photos"
          />
          <NavIcon
            to="/settings"
            icon={<Settings size={iconSize} color={iconColor} />}
            label="Settings"
          />
        </nav>

        {/* Sign out at bottom */}
        <button
          onClick={signOut}
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors"
          title="Sign Out"
        >
          <LogOut size={iconSize} color={iconColor} />
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 bg-gray-50 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
